import React, { useState } from "react";
import BreadCrumb from "../components/BreadCrumb";
import Meta from "../components/Meta";
import { AiOutlineHome, AiOutlineMail } from "react-icons/ai";
import { BiPhoneCall, BiInfoCircle } from "react-icons/bi";
import Container from "../components/Container";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { createQuery } from "../features/contact/contactSlice";

let contactSchema = yup.object({
  name: yup
    .string()
    .required("Name is Required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must be less than 50 characters")
    .matches(/^[a-zA-Z\s]+$/, "Name can only contain letters and spaces"),
  email: yup
    .string()
    .required("Email is Required")
    .email("Email Should be valid")
    .max(100, "Email must be less than 100 characters"),
  mobile: yup
    .string()
    .required("Mobile No is Required")
    .matches(/^\d{10,15}$/, "Mobile number must be between 10 and 15 digits"),
  comment: yup
    .string()
    .required("Comment is Required")
    .min(10, "Comment must be at least 10 characters")
    .max(1000, "Comment must be less than 1000 characters"),
});

const Contact = () => {
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      mobile: "",
      comment: "",
    },
    validationSchema: contactSchema,
    onSubmit: (values) => {
      dispatch(createQuery(values));
    },
  });

  // Schema markup for structured data
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact VEDA Merchandise",
    "description": "Get in touch with VEDA Merchandise for inquiries about our mythological toys, games, and puzzles.",
    "url": "https://vedamerchandise.com/contact",
    "publisher": {
      "@type": "Organization",
      "name": "VEDA Merchandise",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vedamerchandise.com/logo.svg"
      }
    }
  };

  return (
    <>
      <Meta 
        title="Contact VEDA Merchandise - Mythological Toys & Educational Games"
        description="Get in touch with VEDA Merchandise for inquiries about our mythological toys, games, and puzzles."
        keywords="contact VEDA, mythological toys contact, educational games inquiry, customer support"
        author="VEDA Merchandise"
        ogTitle="Contact VEDA Merchandise - Mythological Toys & Educational Games"
        ogDescription="Get in touch with VEDA Merchandise for inquiries about our mythological toys, games, and puzzles."
        ogImage="https://vedamerchandise.com/contact-preview.jpg"
        ogUrl="https://vedamerchandise.com/contact"
        ogType="website"
        twitterCard="summary_large_image"
        twitterTitle="Contact VEDA Merchandise - Mythological Toys & Educational Games"
        twitterDescription="Get in touch with VEDA Merchandise for inquiries about our mythological toys, games, and puzzles."
        twitterImage="https://vedamerchandise.com/contact-preview.jpg"
        robots="index, follow"
        canonicalUrl="https://vedamerchandise.com/contact"
        schemaMarkup={schemaMarkup}
      />
      <BreadCrumb title="Contact Us" />
      <Container class1="contact-wrapper py-5 home-wrapper-2">
        <div className="row">

          {/* Contact Form and Info Section */}
          <div className="col-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              {/* Contact Form */}
              <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <h3 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-6">
                  Send Us a Message
                </h3>
                <form
                  onSubmit={formik.handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div>
                    <input
                      type="text"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none text-gray-700 placeholder-gray-400"
                      placeholder="Your Name"
                      name="name"
                      onChange={formik.handleChange("name")}
                      onBlur={formik.handleBlur("name")}
                      value={formik.values.name}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <div className="text-red-500 text-sm mt-1 ml-1">
                        {formik.errors.name}
                      </div>
                    )}
                  </div>

                  <div>
                    <input
                      type="email"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none text-gray-700 placeholder-gray-400"
                      placeholder="Your Email"
                      name="email"
                      onChange={formik.handleChange("email")}
                      onBlur={formik.handleBlur("email")}
                      value={formik.values.email}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <div className="text-red-500 text-sm mt-1 ml-1">
                        {formik.errors.email}
                      </div>
                    )}
                  </div>

                  <div>
                    <input
                      type="tel"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none text-gray-700 placeholder-gray-400"
                      placeholder="Mobile Number"
                      name="mobile"
                      onChange={formik.handleChange("mobile")}
                      onBlur={formik.handleBlur("mobile")}
                      value={formik.values.mobile}
                    />
                    {formik.touched.mobile && formik.errors.mobile && (
                      <div className="text-red-500 text-sm mt-1 ml-1">
                        {formik.errors.mobile}
                      </div>
                    )}
                  </div>

                  <div>
                    <textarea
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none text-gray-700 placeholder-gray-400 resize-y min-h-[120px]"
                      placeholder="Your Message"
                      name="comment"
                      onChange={formik.handleChange("comment")}
                      onBlur={formik.handleBlur("comment")}
                      value={formik.values.comment}
                      rows="5"
                    ></textarea>
                    {formik.touched.comment && formik.errors.comment && (
                      <div className="text-red-500 text-sm mt-1 ml-1">
                        {formik.errors.comment}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-red-500 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                  >
                    Send Message
                  </button>
                </form>
              </div>

              {/* Contact Info */}
              <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <h3 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-6">
                  Get in Touch
                </h3>
                <ul className="space-y-6">
                  <li className="flex gap-4 items-start group">
                    <div className="bg-gray-100 group-hover:bg-red-500 p-3 rounded-lg flex items-center justify-center min-w-[48px] h-12 transition-all duration-300">
                      <AiOutlineHome className="text-xl text-gray-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800 mb-1">Address</p>
                      <address className="text-gray-600 text-sm md:text-base not-italic leading-relaxed">
                        21ST FLOOR, 2104, C- WING RAHEJA RIDGEWOOD,<br />
                        Goregaon East Mumbai, Maharashtra 400063, India
                      </address>
                    </div>
                  </li>

                  <li className="flex gap-4 items-start group">
                    <div className="bg-gray-100 group-hover:bg-red-500 p-3 rounded-lg flex items-center justify-center min-w-[48px] h-12 transition-all duration-300">
                      <BiPhoneCall className="text-xl text-gray-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800 mb-1">Phone</p>
                      <span className="text-gray-600 text-sm md:text-base">
                        Not Provided
                      </span>
                    </div>
                  </li>

                  <li className="flex gap-4 items-start group">
                    <div className="bg-gray-100 group-hover:bg-red-500 p-3 rounded-lg flex items-center justify-center min-w-[48px] h-12 transition-all duration-300">
                      <AiOutlineMail className="text-xl text-gray-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800 mb-1">Email</p>
                      <a
                        href="mailto:tech@vedamerchandise.com"
                        className="text-blue-600 hover:text-blue-800 hover:underline text-sm md:text-base transition-colors duration-200"
                      >
                        tech@vedamerchandise.com
                      </a>
                    </div>
                  </li>

                  <li className="flex gap-4 items-start group">
                    <div className="bg-gray-100 group-hover:bg-red-500 p-3 rounded-lg flex items-center justify-center min-w-[48px] h-12 transition-all duration-300">
                      <BiInfoCircle className="text-xl text-gray-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800 mb-1">
                        Working Hours
                      </p>
                      <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                        Monday – Friday<br />
                        10:00 AM – 8:00 PM
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default Contact;